/**
 * Conteúdo da aba "Interesses" no LeadDetailsDialog
 * Exibe documentos de banco de dados que o lead tem interesse
 *
 * As colunas usam container query (`@xl:`/`@4xl:`), medidas contra o painel
 * `.ds-drw` do Drawer (`container-type: inline-size` em index.css). Se este
 * componente for renderizado FORA de um drawer, não há container e o grid cai
 * para 1 coluna — nesse caso, envolva num ancestral com `@container`.
 */

import React, { useState } from 'react';
import { getLeadChannelLabel, getLeadMediumLabel, getLeadSourceLabel } from '@troia-v3/types';
import { useLeadInterests, useAddLeadInterests, useRemoveLeadInterest, useApproveLeadInterest } from '../hooks/useLeadInterests';
import { InterestCard } from './InterestCard';
import { AddInterestModal } from './AddInterestModal';
import { ListPageLoading } from '@/shared/components/ui';
import { PopulatedLead } from '../types';
import { Icon } from '@/shared/icons'

// Labels (CHANNEL/SOURCE/MEDIUM) vêm do `@troia-v3/types` — single source of truth
// alinhado com `Lead.channel` / `Lead.source` / `Lead.medium` enums canônicos.

export interface LeadInterestsTabProps {
  leadId: string;
  lead?: PopulatedLead | null;
  onSuccess?: () => void;
}

export const LeadInterestsTab: React.FC<LeadInterestsTabProps> = ({ leadId, lead, onSuccess }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const { data: interests, isLoading } = useLeadInterests(leadId);
  const addMutation = useAddLeadInterests(leadId);
  const removeMutation = useRemoveLeadInterest(leadId);
  const approveMutation = useApproveLeadInterest(leadId);

  const handleAdd = async (documentIds: string[]) => {
    await addMutation.mutateAsync(documentIds);
    setShowAddModal(false);
    onSuccess?.();
  };

  const handleRemove = async (documentId: string) => {
    setRemovingId(documentId);
    try {
      await removeMutation.mutateAsync(documentId);
      onSuccess?.();
    } finally {
      setRemovingId(null);
    }
  };

  const handleApprove = async (documentId: string) => {
    setApprovingId(documentId);
    try {
      await approveMutation.mutateAsync(documentId);
      onSuccess?.();
    } finally {
      setApprovingId(null);
    }
  };

  const existingInterestIds = (interests || []).map((doc: any) => doc.id);

  // Verificar se há alguma informação de origem/fonte
  const hasOriginData = lead && (lead.channel || lead.origin || lead.source || lead.medium || lead.type || lead.campaignName || lead.adsetName || lead.adName);

  return (
    <div className="space-y-6">
      {/* Seção: Origem & Fonte */}
      {hasOriginData && (
        <div>
          <h3
            className="text-sm font-semibold mb-3 flex items-center gap-2"
            style={{ color: '#1e293b' }}
          >
            <Icon name="globe" className="w-4 h-4" />
            Origem & Fonte
          </h3>
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb'
            }}
          >
            <div className="grid grid-cols-2 @4xl:grid-cols-3 gap-4">
              {lead.channel && (
                <div className="flex items-start gap-2.5">
                  <Icon name="globe" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#64748b' }} />
                  <div>
                    <p className="text-xs" style={{ color: '#64748b' }}>Canal</p>
                    <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                      {getLeadChannelLabel(lead.channel)}
                    </p>
                  </div>
                </div>
              )}

              {lead.origin && (
                <div className="flex items-start gap-2.5">
                  <Icon name="globe" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#64748b' }} />
                  <div>
                    <p className="text-xs" style={{ color: '#64748b' }}>Origem</p>
                    <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                      {lead.origin}
                    </p>
                  </div>
                </div>
              )}

              {lead.source && (
                <div className="flex items-start gap-2.5">
                  <Icon name="share" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#64748b' }} />
                  <div>
                    <p className="text-xs" style={{ color: '#64748b' }}>Fonte</p>
                    <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                      {getLeadSourceLabel(lead.source)}
                    </p>
                  </div>
                </div>
              )}

              {lead.medium && (
                <div className="flex items-start gap-2.5">
                  <Icon name="share" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#64748b' }} />
                  <div>
                    <p className="text-xs" style={{ color: '#64748b' }}>Meio</p>
                    <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                      {getLeadMediumLabel(lead.medium)}
                    </p>
                  </div>
                </div>
              )}

              {lead.type && (
                <div className="flex items-start gap-2.5">
                  <Icon name="tag" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#64748b' }} />
                  <div>
                    <p className="text-xs" style={{ color: '#64748b' }}>Tipo</p>
                    <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                      {lead.type}
                    </p>
                  </div>
                </div>
              )}

              {lead.campaignName && (
                <div className="flex items-start gap-2.5">
                  <Icon name="target" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#64748b' }} />
                  <div>
                    <p className="text-xs" style={{ color: '#64748b' }}>Campanha</p>
                    <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                      {lead.campaignName}
                    </p>
                  </div>
                </div>
              )}

              {lead.adsetName && (
                <div className="flex items-start gap-2.5">
                  <Icon name="layers" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#64748b' }} />
                  <div>
                    <p className="text-xs" style={{ color: '#64748b' }}>Conjunto de Anúncio</p>
                    <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                      {lead.adsetName}
                    </p>
                  </div>
                </div>
              )}

              {lead.adName && (
                <div className="flex items-start gap-2.5">
                  <Icon name="file-text" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#64748b' }} />
                  <div>
                    <p className="text-xs" style={{ color: '#64748b' }}>Anúncio</p>
                    <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                      {lead.adName}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h3
          className="text-sm font-semibold flex items-center gap-2"
          style={{ color: '#1e293b' }}
        >
          <Icon name="star" className="w-4 h-4" />
          Interesses {interests && interests.length > 0 && `(${interests.length})`}
        </h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 text-white"
          style={{ backgroundColor: '#8b5cf6' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#7c3aed'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
        >
          <Icon name="plus" className="w-4 h-4" />
          Adicionar interesse
        </button>
      </div>

      {/* Content */}
      {isLoading && <ListPageLoading message="Carregando interesses..." />}

      {!isLoading && (!interests || interests.length === 0) && (
        <div
          className="flex flex-col items-center justify-center py-12 rounded-lg"
          style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}
        >
          <Icon name="star" className="w-10 h-10 mb-3" style={{ color: '#d1d5db' }} />
          <p className="text-sm font-medium" style={{ color: '#64748b' }}>
            Nenhum interesse adicionado
          </p>
          <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
            Clique em "Adicionar interesse" para associar documentos a este lead
          </p>
        </div>
      )}

      {!isLoading && interests && interests.length > 0 && (
        <div className="grid grid-cols-1 @xl:grid-cols-2 @4xl:grid-cols-3 gap-4">
          {interests.map((doc: any) => (
            <InterestCard
              key={doc.id}
              document={doc}
              interestStatus={doc.interestStatus || 'approved'}
              onRemove={() => handleRemove(doc.id)}
              onApprove={() => handleApprove(doc.id)}
              isRemoving={removingId === doc.id}
              isApproving={approvingId === doc.id}
            />
          ))}
        </div>
      )}

      {/* Add Interest Modal — Modal shared já portala pro body */}
      {showAddModal && (
        <AddInterestModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          leadId={leadId}
          existingInterestIds={existingInterestIds}
          onAdd={handleAdd}
        />
      )}
    </div>
  );
};
